/**
 * Frameworks tab of the school management screen (ADM-8): coaching
 * frameworks extracted from uploaded documents, with upload, edit and delete.
 * State and actions come from useSchoolFrameworks.
 */
import React from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { brand } from '../../../design-system/theme/tokens';
import { useTranslation } from '../../../hooks/useTranslation';
import { isSchoolAdmin, type User } from '../../../utils/roleUtils';
import { COLORS } from '../schoolManagementTypes';
import { frameworkDisplayName, frameworkExtractionTotals } from '../schoolRoster';
import type { SchoolFrameworksState } from '../useSchoolFrameworks';
import { PrivateKnowledgeLocked } from './PrivateKnowledgeLocked';

export function FrameworksTab({
  user,
  canAccessPrivateSchoolKnowledge,
  frameworksState,
}: {
  user?: User;
  canAccessPrivateSchoolKnowledge: boolean;
  frameworksState: SchoolFrameworksState;
}) {
  const { t, language } = useTranslation();
  const {
    frameworks,
    loadingFrameworks,
    uploadingFramework,
    deletingFrameworkId,
    handleUploadFramework,
    handleDeleteFramework,
    openFrameworkEditor,
  } = frameworksState;

  if (!canAccessPrivateSchoolKnowledge) {
    return <PrivateKnowledgeLocked />;
  }

  return (
    <View className="mb-6">
      {/* Header with Upload Button */}
      <View className="flex-row justify-between items-center mb-4">
        <View>
          <Text className="text-xl font-bold text-foreground">
            {t('schoolManagement.frameworksSection.title')}
          </Text>
          <Text className="text-sm text-muted-foreground mt-1">
            {t('schoolManagement.frameworksSection.subtitle')}
          </Text>
        </View>
        {isSchoolAdmin(user) && (
          <Button
            variant="default"
            size="sm"
            onPress={() => handleUploadFramework('tr')}
            disabled={uploadingFramework}
          >
            {uploadingFramework ? (
              <>
                <ActivityIndicator size="small" color="white" />
                <Text className="text-white text-xs ml-2">
                  {t('common.loading')}
                </Text>
              </>
            ) : (
              <>
                <MaterialIcons name="cloud-upload" size={16} color="white" />
                <Text className="text-white text-xs ml-2">
                  {t('schoolManagement.frameworksSection.uploadFramework')}
                </Text>
              </>
            )}
          </Button>
        )}
      </View>

      {/* Info Card */}
      <Card className="mb-4 bg-blue-50">
        <CardContent className="p-4">
          <View className="flex-row items-start">
            <MaterialIcons name="auto-stories" size={20} color={brand.primary} />
            <View className="flex-1 ml-3">
              <Text className="text-sm text-gray-700 font-medium mb-1">
                {t('schoolManagement.frameworksSection.infoTitle')}
              </Text>
              <Text className="text-xs text-gray-600">
                {t('schoolManagement.frameworksSection.infoDescription')}
              </Text>
            </View>
          </View>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loadingFrameworks ? (
        <Card>
          <CardContent className="p-8 items-center">
            <ActivityIndicator size="large" color={COLORS.primary} />
            <Text className="text-muted-foreground mt-4">
              {t('schoolManagement.frameworksSection.loading')}
            </Text>
          </CardContent>
        </Card>
      ) : frameworks.length === 0 ? (
        /* Empty State */
        <Card>
          <CardContent className="p-8 items-center">
            <MaterialIcons name="auto-stories" size={64} color="#9CA3AF" />
            <Text className="text-lg font-bold text-foreground mt-4 mb-2">
              {t('schoolManagement.frameworksSection.emptyTitle')}
            </Text>
            <Text className="text-sm text-muted-foreground text-center mb-4">
              {t('schoolManagement.frameworksSection.emptySubtitle')}
            </Text>
            {isSchoolAdmin(user) && (
              <Button variant="default" size="sm" onPress={() => handleUploadFramework('tr')}>
                <MaterialIcons name="cloud-upload" size={16} color="white" />
                <Text className="text-white ml-2">
                  {t('schoolManagement.frameworksSection.uploadFirst')}
                </Text>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Framework List */
        <View className="gap-3">
          {frameworks.map((fw) => {
            const totals = frameworkExtractionTotals(fw);
            return (
              <Card key={fw.id}>
                <CardContent className="p-4">
                  <View className="flex-row items-start justify-between">
                    <View className="flex-1">
                      {/* Framework Name */}
                      <View className="flex-row items-center mb-2">
                        <MaterialIcons name="auto-stories" size={24} color={COLORS.primary} />
                        <Text className="text-base font-semibold text-foreground ml-2 flex-1">
                          {frameworkDisplayName(fw, language)}
                        </Text>
                      </View>

                      {/* Badges */}
                      <View className="flex-row flex-wrap gap-2 mb-2">
                        {/* Type Badge */}
                        <View className="px-2 py-1 rounded bg-indigo-100">
                          <Text className="text-xs font-semibold text-indigo-700">
                            {fw.framework_type === 'step_model'
                              ? t('schoolManagement.frameworksSection.typeStepModel')
                              : t('schoolManagement.frameworksSection.typeCompetencyModel')}
                          </Text>
                        </View>

                        {/* Approved Badge */}
                        <View className={`px-2 py-1 rounded ${fw.is_approved ? 'bg-green-100' : 'bg-yellow-100'}`}>
                          <Text className={`text-xs font-semibold ${fw.is_approved ? 'text-green-700' : 'text-yellow-700'}`}>
                            {fw.is_approved
                              ? t('schoolManagement.frameworksSection.statusApproved')
                              : t('schoolManagement.frameworksSection.statusDraft')}
                          </Text>
                        </View>

                        {/* Active Badge */}
                        {!fw.is_active && (
                          <View className="px-2 py-1 rounded bg-gray-100">
                            <Text className="text-xs font-semibold text-gray-500">
                              {t('schoolManagement.frameworksSection.statusInactive')}
                            </Text>
                          </View>
                        )}

                        {/* Step Count */}
                        <View className="flex-row items-center">
                          <MaterialIcons name="layers" size={14} color="#6b7280" />
                          <Text className="text-xs text-muted-foreground ml-1">
                            {t('schoolManagement.frameworksSection.stepCount', {
                              count: fw.steps?.length || 0,
                            })}
                          </Text>
                        </View>
                      </View>

                      <Text className="text-xs text-muted-foreground mb-2">
                        {fw.framework_type === 'step_model'
                          ? t('schoolManagement.frameworksSection.typeStepModelDescription')
                          : t('schoolManagement.frameworksSection.typeCompetencyModelDescription')}
                      </Text>

                      <Text className="text-xs text-gray-600 mb-2">
                        {t('schoolManagement.frameworksSection.extractionDetailsSummary', {
                          subSteps: totals.subSteps,
                          concepts: totals.concepts,
                          positive: totals.positive,
                          negative: totals.negative,
                          questions: totals.questions,
                        })}
                      </Text>

                      {/* Created At */}
                      <View className="flex-row items-center">
                        <MaterialIcons name="access-time" size={14} color="#6b7280" />
                        <Text className="text-xs text-muted-foreground ml-1">
                          {new Date(fw.created_at).toLocaleDateString()}
                        </Text>
                      </View>
                    </View>

                    {/* Actions */}
                    {isSchoolAdmin(user) && (
                      <View className="ml-3 gap-2">
                        <TouchableOpacity
                          onPress={() => openFrameworkEditor(fw.id)}
                          className="p-2 rounded-lg bg-blue-50"
                        >
                          <MaterialIcons name="edit" size={20} color={COLORS.primary} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleDeleteFramework(fw.id, frameworkDisplayName(fw, language))}
                          disabled={deletingFrameworkId === fw.id}
                          className={`p-2 rounded-lg ${deletingFrameworkId === fw.id ? 'bg-red-100 opacity-60' : 'bg-red-50'}`}
                        >
                          {deletingFrameworkId === fw.id ? (
                            <ActivityIndicator size="small" color={COLORS.error} />
                          ) : (
                            <MaterialIcons name="delete-outline" size={20} color={COLORS.error} />
                          )}
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                </CardContent>
              </Card>
            );
          })}
        </View>
      )}
    </View>
  );
}
